/**
 * The reach HUD model (plan R15): every M3 state the player needs in flight, computed per frame from the reach
 * runtime (travel, scan, relink, nav, interaction, session, state), the player's interpolated float64 position and
 * the live camera into one preallocated `ReachHudState`. Pure of the DOM: view.ts turns it into element writes.
 *
 * Allocation (D-014): the per-frame path writes numbers and cached strings only. Strings that depend on the galaxy,
 * the layout or the state (names, labels, prompts, the banner) are rebuilt by the `refresh…` helpers only when a
 * cheap key changes (a new nav target, a new structure list, a state flip, an event), which is rare.
 */
import { EVENT_MASK, firstReadable } from '../../combat/events'
import { fleetAlertInbox } from '../../fleet-link/presence'
import type { SystemLayout } from '../../galaxy/layout/types'
import type { InputDevices } from '../../input/devices'
import { REV } from '../../reach/events'
import type { ReachCore, TravelMode } from '../../reach/runtime/types'
import { PART_IDS, type PartId, type RelayStatus } from '../../reach/types'
import type { TravelConfig } from '../../travel/config'
import { Life, Pos64, PrevPos64, Vel } from '../../world/components'
import { createScreenPoint, projectDirection, projectWorld, type HudCamera, type ScreenPoint } from '../projection'
import type { ReachHudConfig } from './config'
import {
  bannerClaimed,
  bannerText,
  knownSystemName,
  laneText,
  MASS_LOCK_HINTS,
  MASS_LOCK_LABELS,
  navTargetName,
  navTargetPosition,
  promptText,
  relayOtherSystem,
  routeText,
  structureLabel,
  type HudNames,
  type ToastWords,
} from './labels'
import { createToastQueue, pushNoticeToast, readFleetAlertToasts, readToastEvents, stepToastsBy, type ToastQueue } from './toasts'

/** What the cruise panel shows. */
export type CruisePanel = 'hidden' | 'ready' | 'charging' | 'cruise' | 'lane' | 'cooldown' | 'locked' | 'interdicted' | 'transit'

/** Where the repair list's required quantities come from (R19's relink subsystem; −1 = not known yet). */
export interface RepairListSource {
  requiredParts(relayId: string, part: PartId): number
}

/** Fixed rules the model displays, resolved once at init from the configs. */
export interface ReachHudRules {
  readonly hud: ReachHudConfig
  readonly travel: TravelConfig
  /** `reach-controls.json` `deepScanRequiresNoTarget`: only then does "Clear target" show. */
  readonly scanNeedsNoTarget: boolean
  /** The keyboard interact glyph ("F") and the pad one ("D-pad ↓ (hold)"). */
  readonly keyGlyph: string
  readonly padGlyph: string
  /** The cruise prompts for the keyboard ("HOLD J TO CRUISE") and the pad, built once from reach-controls.json. */
  readonly cruiseKeyHint: string
  readonly cruisePadHint: string
  readonly names: HudNames
}

/** What the model reads each frame. The feature refills one instance in place. */
export interface ReachHudSource {
  core: ReachCore
  playerEid: number
  /** Interpolation factor between the previous and the current tick. */
  alpha: number
  /** Real seconds since the last frame; HUD timers advance by it unless paused. */
  dt: number
  paused: boolean
  cam: HudCamera
  /** The pad was the last device used (prompt glyphs). */
  padActive: boolean
  repair: RepairListSource | null
}

export interface MarkerState {
  on: boolean
  name: string
  distance: number
  /** Float64 world position. */
  x: number
  y: number
  z: number
  point: ScreenPoint
}

/** Far nav marker kinds (D-249): each has its own shape in the view. */
export const FAR_LANE_RING = 0
export const FAR_RELAY = 1

/** The far nav markers: the system's lane rings and relays beyond the label range, nearest first (D-249). */
export interface FarMarkers {
  /** The layer shows (false while cruising flat out through a busy sky). */
  on: boolean
  count: number
  kind: Uint8Array
  distance: Float64Array
  /** Screen position: the point on screen, or the edge position when off screen. */
  x: Float32Array
  y: Float32Array
  /** 1 = off screen, clamped to the edge with an arrow at `angle`. */
  edge: Uint8Array
  angle: Float32Array
}

export interface ReachHudState {
  /** The session is ready and the player alive: the flight panels show. */
  on: boolean
  nav: MarkerState & { route: boolean }
  /** The route's next relay, when the nav target is something else. */
  route: MarkerState
  labelCount: number
  labelX: Float32Array
  labelY: Float32Array
  labelDistance: Float64Array
  labelText: string[]
  labelKind: string[]
  far: FarMarkers
  cruise: {
    panel: CruisePanel
    charge01: number
    speed: number
    etaS: number
    throttle: number
    cooldownS: number
    lockText: string
    /** The cruise prompt or why the drive is blocked ("HOLD J TO CRUISE", "FLY CLEAR TO CRUISE"); empty for none. */
    hint: string
    laneText: string
    ring: number
    rings: number
  }
  interdiction: { on: boolean; point: ScreenPoint; escape01: number; pull01: number; timeLeftS: number }
  relay: {
    on: boolean
    dest: string
    status: RelayStatus
    distance: number
    aligned: boolean
    slowEnough: boolean
    repairOn: boolean
    /** Per part in `PART_IDS` order. `required` −1 = not known yet. */
    delivered: Float64Array
    required: Float64Array
  }
  scan: { on: boolean; fill01: number; pingOn: boolean; ping01: number; blocked: string }
  relink: { on: boolean; hull01: number; timeLeftS: number; wave: number; waves: number }
  prompt: { on: boolean; key: string; text: string; blocked: string }
  banner: { on: boolean; age: number; opacity: number; name: string; sub: string }
  toasts: ToastQueue
  /** Internal caches (keys of the rare-path strings above). */
  readonly cache: HudCache
}

interface HudCache {
  cursor: number
  pingAge: number
  layout: SystemLayout | null
  structuresVersion: number
  discovered: number
  labels: (string | null)[]
  labelStates: Int32Array
  navKind: string
  navId: string
  navRouteRelay: number
  navHasPos: boolean
  navPos: { x: number; y: number; z: number }
  routeRelay: number
  laneIndex: number
  relayIndex: number
  relayId: string
  promptKind: string
  promptIndex: number
  words: ToastWords
  /** A subsystem that keeps free-text HUD notices (R18's exploration flavour and reward details), or null. */
  noticeSource: NoticeSource | null
  /** The last notice `seq` shown. */
  noticeSeq: number
  /** Far markers' layout indices, parallel to `far.kind` (lane ring or relay index). */
  farIndex: Int32Array
}

/** A subsystem with HUD notices, oldest first, each with a rising `seq` (structural: `ExplorationSubsystem.notices`). */
interface NoticeSource {
  readonly notices: readonly { readonly seq: number; readonly text: string }[]
}

function findNoticeSource(core: ReachCore): NoticeSource | null {
  for (const sub of core.subsystems) {
    const n = (sub as unknown as Partial<NoticeSource>).notices
    if (Array.isArray(n)) return sub as unknown as NoticeSource
  }
  return null
}

/** Queues every notice newer than the last one shown (rare: a notice builds a toast). Allocation-free otherwise. */
function readNotices(s: ReachHudState): void {
  const src = s.cache.noticeSource
  if (src === null) return
  const list = src.notices
  for (let i = 0; i < list.length; i++) {
    const n = list[i]
    if (n.seq <= s.cache.noticeSeq) continue
    s.cache.noticeSeq = n.seq
    pushNoticeToast(s.toasts, n.text)
  }
}

function marker(): MarkerState {
  return { on: false, name: '', distance: 0, x: 0, y: 0, z: 0, point: createScreenPoint() }
}

export function createReachHudState(core: ReachCore, cfg: ReachHudConfig): ReachHudState {
  const max = cfg.labels.max
  const labelText: string[] = []
  const labelKind: string[] = []
  for (let i = 0; i < max; i++) {
    labelText.push('')
    labelKind.push('station')
  }
  const words: ToastWords = {
    galaxy: core.galaxy,
    layoutOfIndex(systemIndex: number): SystemLayout | null {
      const sys = core.galaxy.systems[systemIndex]
      if (!sys) return null
      if (core.session.layout?.systemId === sys.id) return core.session.layout
      return core.layoutOf(sys.id)
    },
  }
  return {
    on: false,
    nav: { ...marker(), route: false },
    route: marker(),
    labelCount: 0,
    labelX: new Float32Array(max),
    labelY: new Float32Array(max),
    labelDistance: new Float64Array(max),
    labelText,
    labelKind,
    far: {
      on: false,
      count: 0,
      kind: new Uint8Array(cfg.farMarkers.max),
      distance: new Float64Array(cfg.farMarkers.max),
      x: new Float32Array(cfg.farMarkers.max),
      y: new Float32Array(cfg.farMarkers.max),
      edge: new Uint8Array(cfg.farMarkers.max),
      angle: new Float32Array(cfg.farMarkers.max),
    },
    cruise: { panel: 'hidden', charge01: 0, speed: 0, etaS: 0, throttle: 1, cooldownS: 0, lockText: '', hint: '', laneText: '', ring: 0, rings: 0 },
    interdiction: { on: false, point: createScreenPoint(), escape01: 0, pull01: 0, timeLeftS: 0 },
    relay: {
      on: false,
      dest: '',
      status: 'online',
      distance: 0,
      aligned: false,
      slowEnough: false,
      repairOn: false,
      delivered: new Float64Array(PART_IDS.length),
      required: new Float64Array(PART_IDS.length).fill(-1),
    },
    scan: { on: false, fill01: 0, pingOn: false, ping01: 0, blocked: '' },
    relink: { on: false, hull01: 0, timeLeftS: 0, wave: 0, waves: 0 },
    prompt: { on: false, key: '', text: '', blocked: '' },
    banner: { on: false, age: 0, opacity: 0, name: '', sub: '' },
    toasts: createToastQueue(cfg),
    cache: {
      cursor: 0,
      pingAge: Infinity,
      layout: null,
      structuresVersion: -1,
      discovered: -1,
      labels: [],
      labelStates: new Int32Array(0),
      navKind: '',
      navId: '',
      navRouteRelay: -2,
      navHasPos: false,
      navPos: { x: 0, y: 0, z: 0 },
      routeRelay: -2,
      laneIndex: -2,
      relayIndex: -2,
      relayId: '',
      promptKind: '',
      promptIndex: -2,
      words,
      noticeSource: findNoticeSource(core),
      noticeSeq: 0,
      farIndex: new Int32Array(cfg.farMarkers.max),
    },
  }
}

/**
 * Frame scratch (D-014): the player's interpolated float64 position (x, y, z). A typed array, not module `let`s: a
 * module or closure variable holds a float as a boxed number, so each frame's write allocated 16 B (D-TBD heap sweep).
 */
const player = new Float64Array(3)
/**
 * The frame's HUD dt (0 while paused), read by the toasts, the banner and the scan ping. Kept here, not passed: a
 * fractional double passed to a call V8 does not inline is boxed, 16 B a frame (D-TBD heap sweep).
 */
const frameDt = new Float64Array(1)
const labelPoint = createScreenPoint()
const farPoint = createScreenPoint()

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

// ---- Rare-path refreshers (may allocate) ----------------------------------------------------------------------

/** Rebuilds the label table when the structure list, the discoveries or a structure's state changed. */
function refreshLabels(s: ReachHudState, core: ReachCore, layout: SystemLayout): void {
  const c = s.cache
  const list = core.session.structures
  if (c.labelStates.length !== list.length) c.labelStates = new Int32Array(list.length)
  c.labels.length = list.length
  for (let i = 0; i < list.length; i++) {
    c.labels[i] = structureLabel(layout, core.galaxy, core.state, list[i])
    c.labelStates[i] = list[i].state
  }
  c.structuresVersion = core.session.structuresVersion
  c.discovered = core.state.discoveredPois[layout.systemId]?.length ?? 0
}

function refreshNav(s: ReachHudState, core: ReachCore, layout: SystemLayout): void {
  const c = s.cache
  const t = core.nav.target
  c.navKind = t === null ? '' : t.kind
  c.navId = t === null ? '' : t.id
  c.navRouteRelay = core.nav.routeNextRelayIndex
  if (t === null) return
  c.navHasPos = navTargetPosition(layout, t, c.navPos)
  const r = t.kind === 'relay' ? layout.relays.find((x) => x.relayId === t.id) : undefined
  const onRoute = r !== undefined && r.index === core.nav.routeNextRelayIndex
  s.nav.route = onRoute
  s.nav.name = onRoute ? routeText(knownSystemName(core.galaxy, core.state, r.otherSystemId)) : navTargetName(layout, core.galaxy, core.state, t)
}

function refreshRoute(s: ReachHudState, core: ReachCore, layout: SystemLayout): void {
  const c = s.cache
  c.routeRelay = core.nav.routeNextRelayIndex
  const r = layout.relays.find((x) => x.index === c.routeRelay)
  if (!r) {
    s.route.name = ''
    return
  }
  s.route.name = routeText(knownSystemName(core.galaxy, core.state, r.otherSystemId))
  s.route.x = r.position[0]
  s.route.y = r.position[1]
  s.route.z = r.position[2]
}

function refreshRelay(s: ReachHudState, core: ReachCore, layout: SystemLayout, relayIndex: number): void {
  const c = s.cache
  c.relayIndex = relayIndex
  const r = layout.relays.find((x) => x.index === relayIndex)
  c.relayId = r ? r.relayId : core.galaxy.relays[relayIndex]?.id ?? ''
  const other = r ? r.otherSystemId : relayOtherSystem(core.galaxy, relayIndex, layout.systemId)
  s.relay.dest = other === null ? 'Unknown system' : knownSystemName(core.galaxy, core.state, other)
}

function refreshBanner(s: ReachHudState, core: ReachCore, rules: ReachHudRules, systemIndex: number): void {
  const sys = core.galaxy.systems[systemIndex]
  const layout = sys === undefined ? null : core.session.layout?.systemId === sys.id ? core.session.layout : core.layoutOf(sys.id)
  const text = bannerText(core.galaxy, layout, rules.names, systemIndex, rules.hud.banner.text, sys === undefined ? null : bannerClaimed(sys.id))
  s.banner.name = text.name
  s.banner.sub = text.sub
  s.banner.age = 0
  s.banner.on = text.name.length > 0
}

// ---- Per frame ------------------------------------------------------------------------------------------------

/** Reads the HUD's own event cursor: the system banner and the scan ping. */
function readHudEvents(s: ReachHudState, core: ReachCore, rules: ReachHudRules): void {
  const ring = core.events
  const c = s.cache
  for (let i = firstReadable(ring, c.cursor); i < ring.head; i++) {
    const slot = i & EVENT_MASK
    const type = ring.type[slot]
    if (type === REV.systemEnter) refreshBanner(s, core, rules, ring.eid[slot])
    else if (type === REV.scanPing) c.pingAge = 0
  }
  c.cursor = ring.head
}

function updateLabels(s: ReachHudState, core: ReachCore, cam: HudCamera, rules: ReachHudRules): void {
  const px = player[0]
  const py = player[1]
  const pz = player[2]
  const c = s.cache
  const list = core.session.structures
  const cfg = rules.hud.labels
  const t = core.nav.target
  const max = s.labelText.length
  let n = 0
  for (let i = 0; i < list.length && i < c.labels.length && n < max; i++) {
    const text = c.labels[i]
    if (text === null) continue
    const st = list[i]
    if (t !== null && st.kind === t.kind && st.id === t.id) continue
    const dx = st.position[0] - px
    const dy = st.position[1] - py
    const dz = st.position[2] - pz
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
    if (d > cfg.rangeM || d < cfg.minDistanceM) continue
    if (!projectWorld(cam, st.position[0], st.position[1], st.position[2], 0, labelPoint)) continue
    s.labelX[n] = labelPoint.x
    s.labelY[n] = labelPoint.y
    s.labelDistance[n] = d
    s.labelText[n] = text
    s.labelKind[n] = st.kind
    n++
  }
  s.labelCount = n
}

/** True when the structure list, its states or the discoveries moved on since the label table was built. */
function labelsStale(s: ReachHudState, core: ReachCore, layout: SystemLayout): boolean {
  const c = s.cache
  if (c.layout !== layout || c.structuresVersion !== core.session.structuresVersion) return true
  if ((core.state.discoveredPois[layout.systemId]?.length ?? 0) !== c.discovered) return true
  const list = core.session.structures
  if (list.length !== c.labelStates.length) return true
  for (let i = 0; i < list.length; i++) if (list[i].state !== c.labelStates[i]) return true
  return false
}

function updateNav(s: ReachHudState, core: ReachCore, cam: HudCamera, layout: SystemLayout, rules: ReachHudRules): void {
  const px = player[0]
  const py = player[1]
  const pz = player[2]
  const c = s.cache
  const nav = core.nav
  const t = nav.target
  const nm = s.nav
  if (t === null) {
    nm.on = false
    if (c.navKind !== '') c.navKind = ''
  } else {
    if (t.kind !== c.navKind || t.id !== c.navId || c.navRouteRelay !== nav.routeNextRelayIndex) refreshNav(s, core, layout)
    // The travel runtime keeps nav.x/y/z (R17); before it has, fall back to the layout's position.
    const fromNav = nav.x !== 0 || nav.y !== 0 || nav.z !== 0
    nm.on = fromNav || c.navHasPos
    if (nm.on) {
      nm.x = fromNav ? nav.x : c.navPos.x
      nm.y = fromNav ? nav.y : c.navPos.y
      nm.z = fromNav ? nav.z : c.navPos.z
      const dx = nm.x - px
      const dy = nm.y - py
      const dz = nm.z - pz
      nm.distance = nav.distance > 0 ? nav.distance : Math.sqrt(dx * dx + dy * dy + dz * dz)
      projectWorld(cam, nm.x, nm.y, nm.z, rules.hud.nav.edgeMarginPx, nm.point)
    }
  }
  // The route's next relay gets its own marker when the nav target is something else.
  const rm = s.route
  const rni = nav.routeNextRelayIndex
  if (rni !== c.routeRelay) refreshRoute(s, core, layout)
  rm.on = rni >= 0 && rm.name.length > 0 && !(nm.on && nm.route)
  if (rm.on) {
    const dx = rm.x - px
    const dy = rm.y - py
    const dz = rm.z - pz
    rm.distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
    projectWorld(cam, rm.x, rm.y, rm.z, rules.hud.nav.edgeMarginPx, rm.point)
  }
}

/**
 * Whether the far markers show (D-249): always, except in cruise or lane travel at or above
 * `farMarkers.hideCruiseThrottle` while more than `farMarkers.busyCount` of them would be up.
 */
export function farMarkersShown(mode: TravelMode, throttle: number, count: number, cfg: ReachHudConfig['farMarkers']): boolean {
  if (mode === 'transit') return false
  const flatOut = (mode === 'cruise' || mode === 'lane') && throttle >= cfg.hideCruiseThrottle
  return !(flatOut && count > cfg.busyCount)
}

/** Inserts candidate (kind, index, d) into the nearest-first far list, dropping the farthest when full. */
function farInsert(s: ReachHudState, n: number, max: number, kind: number, index: number, d: number): number {
  const f = s.far
  const idx = s.cache.farIndex
  let at = n
  while (at > 0 && f.distance[at - 1] > d) at--
  if (at >= max) return n
  const last = n < max ? n : max - 1
  for (let j = last; j > at; j--) {
    f.distance[j] = f.distance[j - 1]
    f.kind[j] = f.kind[j - 1]
    idx[j] = idx[j - 1]
  }
  f.distance[at] = d
  f.kind[at] = kind
  idx[at] = index
  return n < max ? n + 1 : n
}

/**
 * The far nav markers (D-249): every lane ring and relay of the system beyond `farMarkers.minDistanceM` (the nearby
 * labels cover what is closer), nearest first, except the nav target and the route relay (they have their own
 * markers). Off-screen ones clamp to the edge like the nav marker. Allocation-free.
 */
function updateFar(s: ReachHudState, core: ReachCore, cam: HudCamera, layout: SystemLayout, rules: ReachHudRules): void {
  const px = player[0]
  const py = player[1]
  const pz = player[2]
  const cfg = rules.hud.farMarkers
  const f = s.far
  const max = f.kind.length
  const t = core.nav.target
  const routeRelay = core.nav.routeNextRelayIndex
  const minD = cfg.minDistanceM
  let n = 0
  const rings = layout.laneRings
  for (let i = 0; i < rings.length; i++) {
    const r = rings[i]
    if (t !== null && t.kind === 'laneRing' && t.id === r.id) continue
    const p = r.position
    const dx = p[0] - px
    const dy = p[1] - py
    const dz = p[2] - pz
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
    if (d >= minD) n = farInsert(s, n, max, FAR_LANE_RING, i, d)
  }
  const relays = layout.relays
  for (let i = 0; i < relays.length; i++) {
    const r = relays[i]
    if (r.index === routeRelay || (t !== null && t.kind === 'relay' && t.id === r.relayId)) continue
    const p = r.position
    const dx = p[0] - px
    const dy = p[1] - py
    const dz = p[2] - pz
    const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
    if (d >= minD) n = farInsert(s, n, max, FAR_RELAY, i, d)
  }
  f.on = n > 0 && farMarkersShown(core.travel.mode, core.travel.throttle, n, cfg)
  f.count = f.on ? n : 0
  const idx = s.cache.farIndex
  const margin = rules.hud.nav.edgeMarginPx
  for (let k = 0; k < f.count; k++) {
    const p = f.kind[k] === FAR_RELAY ? relays[idx[k]].position : rings[idx[k]].position
    projectWorld(cam, p[0], p[1], p[2], margin, farPoint)
    const onScreen = farPoint.onScreen
    f.edge[k] = onScreen ? 0 : 1
    f.x[k] = onScreen ? farPoint.x : farPoint.edgeX
    f.y[k] = onScreen ? farPoint.y : farPoint.edgeY
    f.angle[k] = farPoint.angle
  }
}

function cruisePanelOf(mode: TravelMode, core: ReachCore): CruisePanel {
  switch (mode) {
    case 'charging':
      return 'charging'
    case 'cruise':
      return 'cruise'
    case 'lane':
      return 'lane'
    case 'interdicted':
      return 'interdicted'
    case 'transit':
      return 'transit'
    default:
      if (core.travel.massLock !== 'none') return 'locked'
      return core.travel.cooldownS > 0 ? 'cooldown' : 'ready'
  }
}

/**
 * The cruise hint: while the drive is ready and the nav target is beyond cruise.promptMinDistanceM (or there is no
 * target), the hold-to-cruise prompt; while blocked, what clears it. Returns fixed strings (no allocation).
 */
export function cruiseHintOf(panel: CruisePanel, core: ReachCore, rules: ReachHudRules, padActive: boolean): string {
  if (panel === 'ready') {
    const far = core.nav.target === null || core.nav.distance > rules.hud.cruise.promptMinDistanceM
    if (!far) return ''
    return padActive ? rules.cruisePadHint : rules.cruiseKeyHint
  }
  if (panel === 'locked') return MASS_LOCK_HINTS[core.travel.massLock]
  if (panel === 'interdicted') return 'STEER INTO THE ESCAPE VECTOR'
  return ''
}

function updateCruise(s: ReachHudState, core: ReachCore, layout: SystemLayout, rules: ReachHudRules, playerEid: number, padActive: boolean): void {
  const px = player[0]
  const py = player[1]
  const pz = player[2]
  const tr = core.travel
  const cr = s.cruise
  cr.panel = cruisePanelOf(tr.mode, core)
  cr.charge01 = clamp01(tr.chargeS / (tr.chargeNeedS > 0 ? tr.chargeNeedS : rules.travel.cruise.chargeS))
  cr.speed = tr.speed
  cr.etaS = tr.etaS
  cr.throttle = clamp01(tr.throttle)
  cr.cooldownS = tr.cooldownS
  cr.lockText = MASS_LOCK_LABELS[tr.massLock]
  cr.hint = cruiseHintOf(cr.panel, core, rules, padActive)
  if (cr.panel !== 'lane') return
  const c = s.cache
  if (tr.laneIndex !== c.laneIndex) {
    c.laneIndex = tr.laneIndex
    cr.laneText = laneText(layout, tr.laneIndex)
  }
  const lane = layout.lanes[tr.laneIndex]
  if (lane === undefined) {
    cr.ring = 0
    cr.rings = 0
    return
  }
  // Rings passed so far, along the direction of travel (the velocity's sign along the lane).
  const f = lane.from.position
  const dir = lane.dir
  const along = (px - f[0]) * dir[0] + (py - f[1]) * dir[1] + (pz - f[2]) * dir[2]
  const forward = playerEid < 0 || Vel.x[playerEid] * dir[0] + Vel.y[playerEid] * dir[1] + Vel.z[playerEid] * dir[2] >= 0
  const rings = lane.ringIndices.length
  let passed = 0
  for (let k = 0; k < rings; k++) {
    const r = layout.laneRings[lane.ringIndices[k]]
    if (r === undefined) continue
    const p = r.position
    const ra = (p[0] - f[0]) * dir[0] + (p[1] - f[1]) * dir[1] + (p[2] - f[2]) * dir[2]
    if (forward ? ra <= along : ra >= along) passed++
  }
  cr.ring = passed < 1 ? 1 : passed
  cr.rings = rings
}

function updateInterdiction(s: ReachHudState, core: ReachCore, cam: HudCamera, rules: ReachHudRules): void {
  const it = core.travel.interdiction
  const out = s.interdiction
  out.on = core.travel.mode === 'interdicted' || it.active
  if (!out.on) return
  out.escape01 = clamp01(it.escape / rules.travel.interdiction.escapeS)
  out.pull01 = clamp01(it.pull)
  out.timeLeftS = it.timeLeftS
  projectDirection(cam, it.ex, it.ey, it.ez, rules.hud.nav.edgeMarginPx, out.point)
}

function updateRelay(s: ReachHudState, core: ReachCore, layout: SystemLayout, rules: ReachHudRules, repair: RepairListSource | null): void {
  const h = core.travel.relayHint
  const out = s.relay
  out.on = h.relayIndex >= 0 && h.distance <= rules.travel.transit.hintRangeM && core.travel.mode !== 'transit'
  if (!out.on) return
  const c = s.cache
  if (h.relayIndex !== c.relayIndex || c.layout !== layout) refreshRelay(s, core, layout, h.relayIndex)
  const rs = c.relayId.length > 0 ? core.state.relays[c.relayId] : undefined
  out.status = rs !== undefined && rs.status === 'reboot' ? 'reboot' : h.online ? 'online' : 'offline'
  out.distance = h.distance
  out.aligned = h.aligned
  out.slowEnough = h.slowEnough
  out.repairOn = out.status === 'offline'
  if (!out.repairOn) return
  for (let i = 0; i < PART_IDS.length; i++) {
    const part = PART_IDS[i]
    out.delivered[i] = rs?.delivered[part] ?? 0
    out.required[i] = repair === null || c.relayId.length === 0 ? -1 : repair.requiredParts(c.relayId, part)
  }
}

function updateScan(s: ReachHudState, core: ReachCore, rules: ReachHudRules): void {
  const sc = core.scan
  const out = s.scan
  const c = s.cache
  c.pingAge += frameDt[0]
  out.fill01 = clamp01(sc.holdS / rules.hud.scan.holdS)
  out.pingOn = c.pingAge < rules.hud.scan.pingS
  out.ping01 = out.pingOn ? c.pingAge / rules.hud.scan.pingS : 0
  const held = core.input.scanHeld
  let blocked = ''
  if (held && sc.blocked === 'combat') blocked = 'IN COMBAT'
  else if (held && sc.blocked === 'travel') blocked = 'TRAVELLING'
  else if (held && sc.blocked === 'target' && rules.scanNeedsNoTarget) blocked = 'CLEAR TARGET'
  out.blocked = blocked
  out.on = out.fill01 > 0 || blocked.length > 0 || out.pingOn
}

function updateRelink(s: ReachHudState, core: ReachCore): void {
  const r = core.relink
  const out = s.relink
  // A relay reboot or (M7 S40) a derelict restart defence: the same hull, timer and wave readout.
  out.on = r.phase !== 'idle'
  if (!out.on) return
  out.hull01 = r.hullMax > 0 ? clamp01(r.hull / r.hullMax) : 0
  out.timeLeftS = r.duration - r.t > 0 ? r.duration - r.t : 0
  out.wave = r.wave
  out.waves = r.waves
}

function updatePrompt(s: ReachHudState, core: ReachCore, layout: SystemLayout, rules: ReachHudRules, padActive: boolean): void {
  const p = core.interaction
  const out = s.prompt
  out.on = p.kind !== 'none'
  if (!out.on) return
  const c = s.cache
  if (p.kind !== c.promptKind || p.index !== c.promptIndex) {
    c.promptKind = p.kind
    c.promptIndex = p.index
    out.text = promptText(layout, p.kind, p.index)
  }
  out.key = padActive ? rules.padGlyph : rules.keyGlyph
  out.blocked = p.blocked ?? ''
}

function updateBanner(s: ReachHudState, rules: ReachHudRules): void {
  const b = s.banner
  if (!b.on) return
  b.age += frameDt[0]
  const cfg = rules.hud.banner
  if (b.age >= cfg.seconds) {
    b.on = false
    b.opacity = 0
    return
  }
  const rest = cfg.seconds - b.age
  const fin = cfg.fadeInS > 0 ? b.age / cfg.fadeInS : 1
  const fout = cfg.fadeOutS > 0 ? rest / cfg.fadeOutS : 1
  b.opacity = clamp01(fin < fout ? fin : fout)
}

/** Recomputes `s` from the runtime, the player and the camera. Allocation-free while nothing it names changes. */
export function updateReachHudModel(s: ReachHudState, src: ReachHudSource, rules: ReachHudRules): void {
  const core = src.core
  frameDt[0] = src.paused ? 0 : src.dt
  readHudEvents(s, core, rules)
  readToastEvents(s.toasts, core.events, s.cache.words, rules.hud)
  readFleetAlertToasts(s.toasts, fleetAlertInbox, rules.hud)
  readNotices(s)
  stepToastsBy(s.toasts, frameDt, rules.hud)
  updateBanner(s, rules)

  const e = src.playerEid
  const layout = core.session.layout
  s.on = layout !== null && core.session.ready && e >= 0 && Life.alive[e] === 1
  if (!s.on || layout === null) {
    s.nav.on = false
    s.route.on = false
    s.labelCount = 0
    s.far.on = false
    s.far.count = 0
    s.cruise.panel = 'hidden'
    s.interdiction.on = false
    s.relay.on = false
    s.scan.on = false
    s.relink.on = false
    s.prompt.on = false
    return
  }
  const a = src.alpha
  player[0] = PrevPos64.x[e] + (Pos64.x[e] - PrevPos64.x[e]) * a
  player[1] = PrevPos64.y[e] + (Pos64.y[e] - PrevPos64.y[e]) * a
  player[2] = PrevPos64.z[e] + (Pos64.z[e] - PrevPos64.z[e]) * a

  if (labelsStale(s, core, layout)) {
    refreshLabels(s, core, layout)
    // Another layout invalidates every name cached against the old one.
    if (s.cache.layout !== layout) {
      s.cache.layout = layout
      s.cache.navKind = ''
      s.cache.routeRelay = -2
      s.cache.laneIndex = -2
      s.cache.relayIndex = -2
      s.cache.promptKind = ''
    }
  }
  updateLabels(s, core, src.cam, rules)
  updateNav(s, core, src.cam, layout, rules)
  updateFar(s, core, src.cam, layout, rules)
  updateCruise(s, core, layout, rules, e, src.padActive)
  updateInterdiction(s, core, src.cam, rules)
  updateRelay(s, core, layout, rules, src.repair)
  updateScan(s, core, rules)
  updateRelink(s, core)
  updatePrompt(s, core, layout, rules, src.padActive)
}

/** Tracks the last-used device for prompt glyphs: any key or mouse button → keyboard; a pad button or stick → pad. */
export function updatePadActive(current: boolean, input: InputDevices, axisThreshold: number): boolean {
  if (input.keys.size > 0 || input.mouseButtons !== 0) return false
  const pad = input.pad
  if (pad === null) return false
  for (let i = 0; i < pad.buttons.length; i++) if (pad.buttons[i].pressed) return true
  for (let i = 0; i < pad.axes.length; i++) {
    const v = pad.axes[i]
    if (v > axisThreshold || v < -axisThreshold) return true
  }
  return current
}
